import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, ConfigTimestamps, LoadingState } from './shared';

const PREDEFINED_STATUSES = [
  'ORDER_PLACED', 'ORDER_CONFIRMED', 'PICKED_UP', 'IN_TRANSIT',
  'OUT_FOR_DELIVERY', 'DELIVERED', 'RTO_INITIATED', 'RTO_DELIVERED',
  'CANCELLED', 'RETURN_INITIATED', 'RETURN_PICKED_UP', 'RETURN_DELIVERED',
];

function parseStatusEntries(val) {
  if (!val) return [];
  const map =
    val.sms_status_text_mapping ||
    val.eligible_order_status_to_order_status_text_for_sms_notification;
  if (!map || typeof map !== 'object') return [];
  return Object.entries(map).map(([status, text]) => ({ status, text }));
}

export function SmsNotificationTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS', reloadKey,
  );

  const [enabled, setEnabled] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [statusTextEntries, setStatusTextEntries] = useState([]);
  const [testingEnabled, setTestingEnabled] = useState(false);
  const [testingReceiver, setTestingReceiver] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeCurrency, setChargeCurrency] = useState('');

  useEffect(() => {
    if (!configValue) {
      setEnabled(false);
      setSenderName('');
      setStatusTextEntries([]);
      setTestingEnabled(false);
      setTestingReceiver('');
      setChargeAmount('');
      setChargeCurrency('');
      return;
    }
    setEnabled(
      configValue.is_sms_notification_enabled === true ||
      configValue.sms_notification_enabled === true
    );
    setSenderName(
      configValue.sms_sender_name ??
      configValue.sms_notification_sender_name ?? ''
    );
    setStatusTextEntries(parseStatusEntries(configValue));
    setTestingEnabled(
      configValue.is_sms_testing_mode_enabled === true ||
      configValue.testing_mode_enabled === true
    );
    setTestingReceiver(
      configValue.sms_testing_receiver_number ??
      configValue.testing_mode_notification_receiver ?? ''
    );
    const charge = configValue.notification_charge;
    const rawAmount = configValue.sms_charge_amount ?? (charge && charge.amount);
    const rawCurrency = configValue.sms_charge_currency ?? (charge && charge.currency);
    setChargeAmount(rawAmount != null ? String(rawAmount) : '');
    setChargeCurrency(rawCurrency ?? '');
  }, [configValue]);

  const updateStatusText = (index, field, value) => {
    setStatusTextEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  };

  const addStatusEntry = () => {
    const used = new Set(statusTextEntries.map((e) => e.status));
    const next = PREDEFINED_STATUSES.find((s) => !used.has(s)) || '';
    setStatusTextEntries((prev) => [...prev, { status: next, text: '' }]);
  };

  const removeStatusEntry = (index) => {
    setStatusTextEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (enabled && !senderName.trim()) {
      alert('Sender name is required when SMS notifications are enabled');
      return;
    }
    const statusMap = {};
    for (const entry of statusTextEntries) {
      if (entry.status && entry.text.trim()) {
        statusMap[entry.status] = entry.text.trim();
      }
    }

    const payload = {
      sms_notification_enabled: enabled,
      sms_notification_sender_name: senderName.trim(),
      eligible_order_status_to_order_status_text_for_sms_notification: statusMap,
      testing_mode_enabled: testingEnabled,
      testing_mode_notification_receiver: testingReceiver.trim(),
    };

    if (chargeAmount !== '') {
      payload.notification_charge = {
        amount: parseFloat(chargeAmount),
        currency: chargeCurrency || 'INR',
      };
    }

    saveConfig({ merchant_sms_notification_configurations_value: payload });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {isNew && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-xs sm:text-sm text-blue-700">
          <Info className="w-4 h-4 shrink-0" />
          No existing configuration - fill the form below and save to create one.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>General</h3>
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 items-start">
            <Toggle
              id="sms-enabled"
              checked={enabled}
              onChange={setEnabled}
              label="SMS Notifications Enabled"
              description="Enable SMS for shipment status updates"
            />
            <div>
              <label className={FORM_STYLES.label}>Sender Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="e.g. MYSHOP"
                className={FORM_STYLES.input}
              />
              <p className="text-[11px] text-slate-400 mt-1">Appears as SMS sender (max 6 chars).</p>
            </div>
          </div>
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Pricing</h3>
          <p className="text-[11px] text-slate-400 mb-2">*Inclusive of GST. Contact support to help.</p>
          <div className="space-y-3">
            <div>
              <label className={FORM_STYLES.label}>Charge (₹)</label>
              <input type="text" value={chargeAmount} readOnly disabled className={FORM_STYLES.inputDisabled} />
            </div>
            <div>
              <label className={FORM_STYLES.label}>Currency</label>
              <input type="text" value={chargeCurrency} readOnly disabled className={FORM_STYLES.inputDisabled} />
            </div>
          </div>
        </div>
      </div>

      <div className={FORM_STYLES.sectionCard}>
        <h3 className={FORM_STYLES.sectionTitle}>Status Text Mapping</h3>
        <p className="text-[11px] text-slate-500 mb-3">Configure the SMS text sent for each shipment status update.</p>

        {statusTextEntries.length === 0 && (
          <p className="text-xs text-slate-500 italic mb-3">No status mappings configured.</p>
        )}

        <div className="space-y-2">
          {statusTextEntries.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2">
                <select
                  value={entry.status}
                  onChange={(e) => updateStatusText(idx, 'status', e.target.value)}
                  className={FORM_STYLES.input + ' text-xs'}
                >
                  <option value="">Select status…</option>
                  {PREDEFINED_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={entry.text}
                  onChange={(e) => updateStatusText(idx, 'text', e.target.value)}
                  placeholder="SMS text for this status"
                  className={FORM_STYLES.input}
                />
              </div>
              <button
                type="button"
                onClick={() => removeStatusEntry(idx)}
                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
              >
                &times;
              </button>
            </div>
          ))}
        </div>

        <button type="button" onClick={addStatusEntry} className={`${FORM_STYLES.btnSecondary} mt-3`}>
          + Add Status Mapping
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={handleSave} disabled={saving} className={FORM_STYLES.btnPrimary}>
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>
    </div>
  );
}
